import { API_URL } from '../../../constants/urls.constants';
import { getErrorMessage } from '../../../lib/error';
import { toast } from '../../../lib/toast';
import type { CommonResponse } from '../../../types/global.types';
import { appServiceApi } from '../../apis/app-service.api';
import { authServiceApi } from '../../apis/auth-service.api';
import { APP_API_TAGS } from '../../app.constants';
import type { UserPermissionResponse } from '../control-panel/role-permission/role-permission.type';
import type { EmployeeData } from '../employee/emp-dashboard/emp-dashboard.type';
import type {
  SubscriptionByAdminIdResponse,
  SubscriptionVerificationResponse,
} from '../onboarding/onboarding.type';
import {
  setModulesInfo,
  setPermissionsInfo,
  setProfileInfo,
  userLoggedOut,
  verifyUser,
} from './auth.slice';
import type { TGetOtpVerify } from './auth.types';

const { EMPLOYEES, SUBSCRIPTIONS, ROLES } = APP_API_TAGS;

export const authApi = authServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    sendOtp: builder.mutation({
      query: (body) => ({
        url: API_URL.sendOtp,
        method: 'POST',
        body,
      }),
    }),
    otpVerify: builder.mutation<CommonResponse<TGetOtpVerify>, unknown>({
      query: (body) => ({
        url: API_URL.otpVerify,
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const res = data?.data;

          const userData = {
            access_token: res?.accessToken,
            user: res?.user,
          };

          // Store verified info, userId, and access_token in localStorage
          localStorage.setItem('verifiedInfo', JSON.stringify(res?.user));
          localStorage.setItem('userId', res?.user?.id);
          localStorage.setItem('access_token', res?.accessToken);

          dispatch(verifyUser(userData));
        } catch (err) {
          dispatch(userLoggedOut());
          localStorage.removeItem('verifiedInfo');
          localStorage.removeItem('userId');
          localStorage.removeItem('access_token');
        }
      },
    }),
    // TODO: OLD data
    uploadFile: builder.mutation({
      query: (data) => {
        return {
          url: API_URL.uploadFile,
          method: 'PATCH',
          body: data,
        };
      },
    }),
    uploadMobileToken: builder.mutation({
      query: (data) => {
        return {
          url: API_URL.uploadMobileToken,
          method: 'POST',
          body: data,
        };
      },
    }),
  }),
});

export const {
  useSendOtpMutation,
  useOtpVerifyMutation,
  useUploadMobileTokenMutation,
  useUploadFileMutation,
} = authApi;

export const authAppApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<CommonResponse<{ data: EmployeeData }>, string>({
      query: (id) => ({
        url: `${API_URL.employees}/get-me/${id}`,
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          const profile = data?.data?.data;
          if (profile) {
            dispatch(setProfileInfo(profile));
          }
        } catch (err) {
          dispatch(userLoggedOut());
        }
      },
      providesTags: (_, __, id) => [
        { type: EMPLOYEES, id: `my-profile-${id}` },
      ],
    }),

    getPermissions: builder.query<UserPermissionResponse, { userId: string }>({
      query: ({ userId }) => ({
        url: `${API_URL.roles}/user/${userId}/permissions`,
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // if (!data?.success || !data?.data?.length) {
          //   dispatch(userLoggedOut());
          //   toast('error', 'You have no access.');
          //   return;
          // }
          if (data?.success && data?.data) {
            dispatch(setPermissionsInfo(data.data));
          }
        } catch (error) {
          dispatch(userLoggedOut());
          console.log(error, 'From permission get');
          toast('error', getErrorMessage(error, 'You have no access.'));
        }
      },
      providesTags: [ROLES],
    }),

    getSubscription: builder.query<SubscriptionByAdminIdResponse, string>({
      query: (adminId) => ({
        url: `${API_URL.subscriptionByUserId}/${adminId}`,
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.data?.length) {
            const firstSubscription = data.data[0];
            const planModules =
              firstSubscription?.plan_details?.planModules || [];

            const modulesInfo = planModules.map((pm: any) => ({
              moduleId: pm.module_id,
              moduleName: pm.module?.name || 'N/A',
            }));

            dispatch(setModulesInfo(modulesInfo));
          }
        } catch (error) {
          toast('error', getErrorMessage(error, 'Subscription not found.'));
        }
      },
      providesTags: [SUBSCRIPTIONS],
    }),

    verifySubscription: builder.query<SubscriptionVerificationResponse, string>(
      {
        query: (adminId) => ({
          url: `${API_URL.subscriptionVerification}/${adminId}`,
          method: 'GET',
        }),
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (err) {
            toast('error', getErrorMessage(err, 'Subscription not found.'));
          }
        },
        providesTags: [{ type: SUBSCRIPTIONS, id: 'verification' }],
      },
    ),
  }),
});

export const {
  useGetProfileQuery,
  useGetPermissionsQuery,
  useGetSubscriptionQuery,
  useVerifySubscriptionQuery,
} = authAppApi;
